import type { IntlShape } from 'react-intl';
import { type AgentPolicy } from '@repo/api/schemas/agentPolicy.schema';

export function getTriggerLabels(intl: IntlShape): Record<string, string> {
  return {
    AGENT_REGISTERED: intl.formatMessage({
      id: 'k1ypYv',
      defaultMessage: 'On agent registration',
      description: 'Trigger label: agent registered',
    }),
    LABEL_MODIFICATION: intl.formatMessage({
      id: '4W1KGZ',
      defaultMessage: 'On label modification',
      description: 'Trigger label: label modification',
    }),
    OWNER_DEACTIVATION: intl.formatMessage({
      id: 'PfcMC1',
      defaultMessage: 'On owner deactivation',
      description: 'Trigger label: owner deactivation',
    }),
  };
}

/**
 * Builds a human-readable rule summary from policy for table display.
 * e.g. "On agent registration · 2 conditions • Assign 2 owners"
 */
export function formatRuleSummary(
  policy: AgentPolicy,
  intl: IntlShape
): string {
  const triggerLabels = getTriggerLabels(intl);
  const triggerLabel =
    triggerLabels[policy.triggerType] ??
    intl.formatMessage(
      {
        id: 'KQj1cw',
        defaultMessage: 'On {triggerType}',
        description: 'Fallback trigger label for unknown trigger types',
      },
      { triggerType: policy.triggerType }
    );

  const conditionCount = Array.isArray(policy.conditions?.conditions)
    ? policy.conditions.conditions.length
    : 0;
  const conditionPart = intl.formatMessage(
    {
      id: 'yM7H5J',
      defaultMessage:
        '{count} {count, plural, one {condition} other {conditions}}',
      description: 'Number of conditions in rule summary',
    },
    { count: conditionCount }
  );

  let actionPart = '';
  if (Array.isArray(policy.actions) && policy.actions.length > 0) {
    const firstAction = policy.actions[0];
    const payloads = firstAction?.payload ?? [];
    const assignmentCount = payloads.reduce(
      (sum, p) => sum + (Array.isArray(p.value) ? p.value.length : 0),
      0
    );
    const vacancyRuleCount = payloads.reduce(
      (sum, p) => sum + (Array.isArray(p.payload) ? p.payload.length : 0),
      0
    );
    if (assignmentCount > 0) {
      actionPart =
        assignmentCount === 1
          ? intl.formatMessage({
              id: 'eL56Rp',
              defaultMessage: 'Assign 1 Primary Owner',
              description: 'Action summary: assign single primary owner',
            })
          : intl.formatMessage(
              {
                id: 'ObU7ZX',
                defaultMessage: 'Assign {count} owners',
                description: 'Action summary: assign multiple owners',
              },
              { count: assignmentCount }
            );
    } else if (vacancyRuleCount > 0) {
      actionPart = intl.formatMessage(
        {
          id: 'GrGC7Q',
          defaultMessage: '{count} rules to handle inactive owners',
          description: 'Action summary: vacancy rules count',
        },
        { count: vacancyRuleCount }
      );
    }
  }

  const suffix = actionPart ? ` • ${actionPart}` : '';
  return `${triggerLabel} · ${conditionPart}${suffix}`;
}
